import { Route, Navigate, useLocation } from 'react-router-dom';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import { CatalogEntityPage, catalogPlugin } from '@backstage/plugin-catalog';
import { catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { techdocsPlugin } from '@backstage/plugin-techdocs';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';

import { AlertDisplay, OAuthRequestDialog } from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';

import { QetaPage } from '@drodil/backstage-plugin-qeta';
import {
  ApiDocsComponent,
  ReferenceDocsPage,
} from '@internal/plugin-reference-docs';
import { CmsPage } from '@internal/plugin-cms';
import { OktaSignInPage, GuestSignInPage } from './components/CustomSignInPage';
import { Dashboard } from './components/Dashboard';
import { Header } from './components/Header';
import { CustomSettingsPage } from './components/Settings';
import { isPublicRoute, ProtectedRoute } from './components/ProtectedRoute';
import { themes, UnifiedThemeProvider } from '@backstage/theme';
import { MarkdownRender } from './components/ChangeLogs';

// Domain check component for jll.com redirection
const DomainRedirectWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const isJllDomain = window.location.hostname.includes('jll.com');

  // If it's jll.com domain and not in development, redirect to dashboard for API docs routes
  if (
    isJllDomain &&
    (location.pathname === '/' ||
      location.pathname === '/redoc' ||
      location.pathname === '/v2' ||
      location.pathname === '/v2/redoc')
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const withHeader = (Component: React.ComponentType<any>, props?: any) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
    }}
  >
    <Header />
    <Component {...props} />
  </div>
);

const app = createApp({
  apis,
  themes: [
    {
      id: 'light',
      title: 'Light',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={themes.light} children={children} />
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => {
      // For public routes, use Guest SignIn
      if (isPublicRoute(window.location.pathname)) {
        return <GuestSignInPage {...props} />;
      }

      // For all other routes (protected), use Okta
      return <OktaSignInPage {...props} />;
    },
  },
});

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <FlatRoutes>
        <Route
          path="/"
          element={
            <DomainRedirectWrapper>
              <ApiDocsComponent version="v1" type="swagger" />
            </DomainRedirectWrapper>
          }
        />
        <Route
          path="/redoc"
          element={
            <DomainRedirectWrapper>
              <ApiDocsComponent version="v1" type="redoc" />
            </DomainRedirectWrapper>
          }
        />
        <Route
          path="/v2"
          element={
            <DomainRedirectWrapper>
              <ApiDocsComponent version="v2" type="swagger" />
            </DomainRedirectWrapper>
          }
        />
        <Route
          path="/v2/redoc"
          element={
            <DomainRedirectWrapper>
              <ApiDocsComponent version="v2" type="redoc" />
            </DomainRedirectWrapper>
          }
        />
        <Route
          path="/catalog/:namespace/:kind/:name"
          element={<CatalogEntityPage />}
        >
          {entityPage}
        </Route>
        {/* BASE ROUTES */}
        <Route path="/dashboard" element={withHeader(Dashboard)} />
        {/* PRISM ROUTES */}
        <Route
          path="/prism/reference-docs"
          element={withHeader(ReferenceDocsPage, {
            version: 'v1',
            type: 'swagger',
          })}
        />
        <Route
          path="/prism/reference-docs/redoc"
          element={withHeader(ReferenceDocsPage, {
            version: 'v1',
            type: 'redoc',
          })}
        />
        <Route
          path="/prism/reference-docs/v2"
          element={withHeader(ReferenceDocsPage, {
            version: 'v2',
            type: 'swagger',
          })}
        />
        <Route
          path="/prism/reference-docs/v2/redoc"
          element={withHeader(ReferenceDocsPage, {
            version: 'v2',
            type: 'redoc',
          })}
        />
        <Route path="/prism/change-logs" element={withHeader(MarkdownRender)} />
        <Route path="/prism/how-to-docs" element={withHeader(CmsPage)} />
        <Route
          path="/prism/forum"
          element={<ProtectedRoute>{withHeader(QetaPage)}</ProtectedRoute>}
        />
        {/* SETTING */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>{withHeader(CustomSettingsPage)}</ProtectedRoute>
          }
        />
      </FlatRoutes>
    </AppRouter>
  </>,
);
